import dayjs from '@dayjs';
import {
  ELanguage,
} from '@qmulus/frontend-shared/type/locale.ts';
import type {
  Dayjs,
} from 'dayjs';
import {
  Duration, DurationUnitType,
} from 'dayjs/plugin/duration';

import {
  useAppStore,
} from '@/store/pinia/app';

export function getFormatFromHours(hours: number, days?: number) {
  if (days !== undefined && Math.floor(days) > 0) {
    return 'D[d] HH:mm';
  }

  if (hours > 0) {
    return 'HH:mm:ss';
  }

  return 'mm:ss';
}

export function getFirstDayOfWeek() {
  const locale = useAppStore().locale;
  let startWeekDay = 1;

  let exhaustiveCheck: never;
  switch (locale) {
    case ELanguage.DE:
    case ELanguage.EN_GB:
    case ELanguage.PL:
    case ELanguage.SK:
      startWeekDay = 1;
      break;
    case ELanguage.EN_US:
      startWeekDay = 7;
      break;
    default:
      exhaustiveCheck = locale;
      throw new Error(`Missing locale: ${exhaustiveCheck}.`);
  }
  // @ts-expect-error: FF doesn't support Intl.Locale().weekInfo
  return new Intl.Locale(locale)?.weekInfo?.firstDay || startWeekDay;
}

export const DAYJS_TIME_FORMAT = 'LTS';
export const DAYJS_TIME_FORMAT_SHORT = 'LT';
export function formatDayJsDateWithoutDate(dayjsDate: Dayjs, short: boolean = false) {
  const format = short ? DAYJS_TIME_FORMAT_SHORT : DAYJS_TIME_FORMAT;
  return dayjsDate.format(format);
}

export const DAYJS_DATE_FORMAT = 'll';
export const DAYJS_DATE_FORMAT_SHORT = 'L';
export function formatDayJsDateWithoutTime(dayjsDate: Dayjs, short: boolean = false) {
  const format = short ? 'L' : 'll';
  return dayjsDate.format(format);
}

export const DATE_TIME_FORMAT = `${DAYJS_DATE_FORMAT}, ${DAYJS_TIME_FORMAT}`;
export const DATE_TIME_FORMAT_SHORT = `${DAYJS_DATE_FORMAT_SHORT}, ${DAYJS_TIME_FORMAT_SHORT}`;
export function formatDayjsDate(dayjsDate: Dayjs, short: boolean = false) {
  const format = short ? DATE_TIME_FORMAT_SHORT : DATE_TIME_FORMAT;
  return dayjsDate.format(format);
}

export function formatDate(date: string | number | Date, short: boolean = false) {
  return formatDayjsDate(dayjs(date), short);
}

export function formatDuration(date: Dayjs) {
  return date.format(getFormatFromHours(date.hour()));
}

export type TFormatDurationConfig = {
  showSeconds?: boolean,
  showMilliseconds?: boolean,
};

export function formatFromDuration(duration: Duration, config: TFormatDurationConfig = {}) {
  const {
    showSeconds = true,
    showMilliseconds = false,
  } = config;
  const durationCopy = duration.asSeconds() >= 0 ? duration : dayjs.duration(Math.abs(duration.asMilliseconds()));
  const negativeSign = duration.asSeconds() < 0 ? '-' : '';
  const secondsTemplate = showSeconds ? ':ss' : '';
  const millisecondsTemplate = (showSeconds && showMilliseconds) ? '.SSS' : '';
  if (durationCopy.years() >= 1) {
    return durationCopy.format(`${negativeSign}Y[y] M[m] D[d] HH:mm${secondsTemplate}${millisecondsTemplate}`);
  }

  if (durationCopy.asDays() >= 31) {
    return durationCopy.format(`${negativeSign}M[m] D[d] HH:mm${secondsTemplate}${millisecondsTemplate}`);
  }

  if (durationCopy.asHours() >= 24) {
    return durationCopy.format(`${negativeSign}${Math.floor(duration.asDays())}[d] HH:mm${secondsTemplate}${millisecondsTemplate}`);
  }

  return durationCopy.format(`${negativeSign}HH:mm${secondsTemplate}${millisecondsTemplate}`);
}

export function formatFromDurationShort(duration: Duration) {
  const days = Math.floor(duration.asDays());
  if (days >= 1) {
    const remainingHours = duration.hours();
    if (remainingHours === 0) {
      return `${days}d`;
    }
    return `${days}d ${remainingHours}h`;
  }

  const hours = duration.asHours();
  if (hours >= 1) {
    if (duration.minutes() === 0) {
      return duration.format('H[h]');
    }
    return duration.format('H[h] m[m]');
  }

  const minutes = duration.asMinutes();
  if (minutes >= 1) {
    if (duration.seconds() === 0) {
      return duration.format('m[m]');
    }
    return duration.format('m[m] s[s]');
  }

  const milliseconds = duration.milliseconds();
  if (duration.asSeconds() < 1) {
    return `${milliseconds.toFixed(0)}ms`;
  }

  if (milliseconds === 0) {
    return duration.format('s[s]');
  }

  return `${duration.format('s[s]')} ${milliseconds.toFixed(0)}ms`;
}

export function formatDurationInSeconds(durationInSeconds: number, config: TFormatDurationConfig = {}) {
  return formatFromDuration(dayjs.duration(durationInSeconds, 'seconds'), config);
}

export function formatDurationQuantity(duration: number, unit: DurationUnitType, config: TFormatDurationConfig = {}) {
  return formatFromDuration(dayjs.duration(duration, unit), config);
}

export function formatMilliseconds(mills: number, showSeconds?: boolean, showMilliseconds?: boolean): string {
  const duration = dayjs.duration(mills);

  let result = `${
    duration.hours()
      .toString()
      .padStart(2, '0')
  }:${
    duration.minutes()
      .toString()
      .padStart(2, '0')
  }`;
  if (showSeconds) {
    result += `:${duration.seconds().toString().padStart(2, '0')}`;
  }

  if (showMilliseconds) {
    result += `:${duration.milliseconds().toString().padStart(4, '0')}`;
  }
  return result;
}
